/**
 * The console and the pages a support report carries, recorded from PAGE LOAD.
 *
 * Imported FIRST in main.tsx, before the app's own modules, so what they log
 * while they load is inside the window — as PanLuma's widget records its own
 * (every level, the last 50 lines). Installing it after the app's imports
 * (as before) caught only warnings and errors logged after start-up: on a
 * quiet page, nothing — the "Console output" column arrived empty (staging,
 * 2026-10-26).
 *
 * Depends only on the pure core, so importing it first pulls in nothing else.
 */

import { CONSOLE_LEVELS, ConsoleRing, NavigationRing } from './supportCaptureCore';

export const consoleRing = new ConsoleRing();
export const navigationRing = new NavigationRing();

function install(): void {
  if (typeof window === 'undefined') return;
  const marked = window as Window & { __corpbrainSupportBuffer?: boolean };
  if (marked.__corpbrainSupportBuffer) return;   // a hot reload keeps the first one
  marked.__corpbrainSupportBuffer = true;

  for (const level of CONSOLE_LEVELS) {
    const original = console[level];
    if (typeof original !== 'function') continue;
    const bound = original.bind(console);
    console[level] = (...parts: unknown[]) => {
      try { consoleRing.push(level, parts); } catch { /* never in the way of the console itself */ }
      bound(...parts);
    };
  }
  window.addEventListener('error', e => consoleRing.push('error', [e.message]));
  window.addEventListener('unhandledrejection', e => consoleRing.push('error', [e.reason]));

  // The app's pages (the router uses history.pushState / replaceState).
  const note = () => { try { navigationRing.push(window.location.href, new Date()); } catch { /* never in the way */ } };
  for (const method of ['pushState', 'replaceState'] as const) {
    const original = window.history[method].bind(window.history);
    window.history[method] = (...args: Parameters<History['pushState']>) => {
      original(...args);
      note();
    };
  }
  window.addEventListener('popstate', note);
  note();
}

install();
